import { ICheckinType } from '../journal/model/ICheckin';
import { IInsightEvidence } from './IInsight';
import { parseInsightRef } from './insightRef';

// Where a tapped insight-evidence row should route to. Pure function of the evidence (ref + date)
// and the viewer's identity -- no DI, no state lookups -- so InsightEvidenceRow's isTappable and
// onClick both derive from the same source of truth and can never disagree (no tappable row that
// dead-ends, no blank drawers).
export type IEvidenceTarget =
    | { kind: 'note'; noteId: string }
    | { kind: 'checkin'; checkinType: ICheckinType; date: string }
    | { kind: 'appointment' }
    | { kind: 'task'; taskId: string }
    | { kind: 'none' };

export const evidenceTarget = (params: {
    evidence: IInsightEvidence;
    currentUserId?: string;
    careMapOwnerUserId?: string;
}): IEvidenceTarget => {
    const { evidence, currentUserId, careMapOwnerUserId } = params;

    const parsed = parseInsightRef(evidence.ref);
    if (!parsed) {
        return { kind: 'none' };
    }

    if (parsed.contentType === 'note') {
        // checkin notes are journal entries, not care-map notes -- they don't have a "View Note"
        // drawer, so route to the day's check-in editor instead (see CheckinState.startEditing)
        if (parsed.noteKind === 'checkin-personal') {
            // Personal check-ins are author-only server-side: a care insight can cite another
            // member's personal check-in (the reflection digest runs as the caremap owner), but the
            // viewer can only ever load their OWN. Refs missing by= predate author stamping; those
            // personal check-ins were always the caremap owner's (care scope) or the viewer's own
            // (private scope, where the viewer IS the owner), so the owner is the right fallback.
            const authorId = parsed.authorId ?? careMapOwnerUserId;
            if (!currentUserId || authorId !== currentUserId) {
                return { kind: 'none' };
            }
            return { kind: 'checkin', checkinType: 'personal', date: evidence.date };
        }
        if (parsed.noteKind === 'checkin-care') {
            // care check-ins are caremap-shared, so any member can open the day's entry
            return { kind: 'checkin', checkinType: 'care', date: evidence.date };
        }
        return { kind: 'note', noteId: parsed.sourceId };
    }

    if (parsed.contentType === 'appointment') {
        return { kind: 'appointment' };
    }

    if (parsed.contentType === 'task') {
        return { kind: 'task', taskId: parsed.sourceId };
    }

    return { kind: 'none' };
};
